import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/library',
  },
  {
    path: '/library',
    name: 'library',
    component: () => import('@/views/LibraryView.vue'),
    meta: { title: 'Biblioteca' },
  },
  {
    path: '/importar',
    name: 'import',
    component: () => import('@/views/ImportView.vue'),
    meta: { title: 'Importar' },
  },
  {
    path: '/nube',
    name: 'cloud',
    component: () => import('@/views/CloudLibraryView.vue'),
    meta: { title: 'En la nube' },
  },
  {
    path: '/book/:id',
    name: 'book-detail',
    component: () => import('@/views/BookDetailView.vue'),
    props: true,
    meta: { title: 'Detalle del libro' },
  },
  {
    path: '/reader/:bookId',
    name: 'reader',
    component: () => import('@/views/ReaderView.vue'),
    props: true,
    meta: { title: 'Lector', fullscreen: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { title: 'Ajustes' },
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('@/views/AuthView.vue'),
    meta: { title: 'Cuenta' },
  },
  {
    path: '/stats',
    name: 'stats',
    component: () => import('@/views/StatsView.vue'),
    meta: { title: 'Estatísticas' },
  },
  {
    path: '/conocimiento',
    name: 'knowledge',
    component: () => import('@/views/KnowledgeView.vue'),
    meta: { title: 'Conocimiento' },
  },
  {
    path: '/repaso',
    name: 'review',
    component: () => import('@/views/ReviewView.vue'),
    meta: { title: 'Repaso' },
  },
  {
    path: '/invitar',
    name: 'invite',
    component: () => import('@/views/InviteView.vue'),
    meta: { title: 'Invitación' },
  },
  {
    path: '/descubrir',
    name: 'discover',
    component: () => import('@/views/DiscoverView.vue'),
    meta: { title: 'Descubrir' },
  },
  {
    path: '/perfil/:id',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    props: true,
    meta: { title: 'Perfil' },
  },
  {
    path: '/clubs',
    name: 'clubs',
    component: () => import('@/views/ClubsView.vue'),
    meta: { title: 'Clubs de lectura' },
  },
  {
    path: '/club/:id',
    name: 'club-detail',
    component: () => import('@/views/ClubDetailView.vue'),
    props: true,
    meta: { title: 'Club de lectura' },
  },
  {
    path: '/notificaciones',
    name: 'notifications',
    component: () => import('@/views/NotificationsView.vue'),
    meta: { title: 'Notificaciones' },
  },

  // ---------- Panel de administración ----------
  // Ruta padre con layout propio (sidebar) y guard de rol. Las hijas se
  // cargan perezosamente y heredan meta.requiresAdmin del padre.
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: { title: 'Administración', requiresAdmin: true },
    children: [
      { path: '', redirect: '/admin/overview' },
      {
        path: 'overview',
        name: 'admin-overview',
        component: () => import('@/views/admin/AdminOverview.vue'),
        meta: { title: 'Resumen · Admin' },
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/views/admin/AdminUsersView.vue'),
        meta: { title: 'Usuarios · Admin' },
      },
      {
        path: 'events',
        name: 'admin-events',
        component: () => import('@/views/admin/AdminEventsView.vue'),
        meta: { title: 'Eventos · Admin' },
      },
      {
        path: 'email',
        name: 'admin-email',
        component: () => import('@/views/admin/AdminEmailView.vue'),
        meta: { title: 'Email · Admin' },
      },
      {
        path: 'audit',
        name: 'admin-audit',
        component: () => import('@/views/admin/AdminAuditView.vue'),
        meta: { title: 'Auditoría · Admin' },
      },
      {
        path: 'moderation',
        name: 'admin-moderation',
        component: () => import('@/views/admin/AdminModerationView.vue'),
        meta: { title: 'Moderación · Admin' },
      },
      {
        path: 'invitations',
        name: 'admin-invitations',
        component: () => import('@/views/admin/AdminInvitationsView.vue'),
        meta: { title: 'Invitaciones · Admin' },
      },
      {
        path: 'system',
        name: 'admin-system',
        component: () => import('@/views/admin/AdminSystemView.vue'),
        meta: { title: 'Sistema · Admin' },
      },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

/**
 * Guard global: las rutas con `meta.requiresAdmin` sólo son accesibles si
 * el usuario autenticado tiene rol admin. Si no hay sesión, va al login;
// si hay sesión pero no es admin, vuelve a la biblioteca.
 *
// Import diferido para no cargar el store de auth en el bundle inicial si
// el usuario nunca navega a /admin.
 */
router.beforeEach(async (to) => {
  if (!to.meta.requiresAdmin && !to.matched.some((r) => r.meta.requiresAdmin)) {
    return true
  }
  const { useAuthStore } = await import('@/sync/auth')
  const auth = useAuthStore()
  if (!auth.isAuthenticated) {
    return { name: 'auth', query: { redirect: to.fullPath } }
  }
  // Asegurar que el perfil esté cargado (p.ej. tras recarga en frío).
  if (!auth.profile) await auth.loadProfile()
  if (!auth.isAdmin) {
    return { name: 'library' }
  }
  return true
})

router.afterEach((to) => {
  const title = (to.meta.title as string | undefined) ?? 'EPUB Unlimited'
  document.title = `${title} · EPUB Unlimited`
})
